import { useCallback, useState, useRef, useEffect } from 'react'
import { useAuth } from './useAuth'
import { fetchWithAuth, AuthExpiredError } from '@/lib/fetchWithAuth'
import { useDashboardStore } from '@/store/dashboardStore'
import {
  parseBlockEvent,
  parseTitleEvent,
  parseSummaryEvent,
  parseErrorEvent,
  parseFinalEvent,
  type ConnectionStatus,
} from '@/types/sse'
import {
  parseNodeStartEvent,
  parseNodeCompleteEvent,
  parseInterruptEvent,
  parseResumedEvent,
  parseEnterpriseStatusEvent,
  parseFallbackEvent,
  type NodeExecution,
  type ApprovalDecision,
  FALLBACK_TIMEOUT_MS,
} from '@/types/orchestrator'

interface UseOrchestratorStreamOptions {
  baseUrl?: string
  sseBaseUrl?: string
  onFallback?: (reason: string) => void
}

interface UseOrchestratorStreamReturn {
  status: ConnectionStatus
  startStream: (query: string, context?: Record<string, unknown>) => void
  stopStream: () => void
  resumeWorkflow: (decision: ApprovalDecision) => Promise<void>
  error: Error | null
  isStreaming: boolean
  isPaused: boolean
}

/**
 * Hook for streaming queries through the Platform Orchestrator
 * Handles multi-agent orchestration, HITL interrupts, and node execution tracking
 *
 * Key differences from useBlockStream:
 * - Routes to /api/orchestrate instead of /agents/{id}/stream
 * - Handles node_start, node_complete, interrupt, resumed events
 * - Supports workflow resume via resumeWorkflow()
 * - Tracks enterprise feature status
 * - Automatic fallback detection
 */
export function useOrchestratorStream(
  options: UseOrchestratorStreamOptions = {}
): UseOrchestratorStreamReturn {
  const { sseBaseUrl = import.meta.env.VITE_ORCHESTRATOR_URL || '/orchestrator' } = options

  const [status, setStatus] = useState<ConnectionStatus>('disconnected')
  const [error, setError] = useState<Error | null>(null)
  const [isStreaming, setIsStreaming] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)
  const streamStartRef = useRef<number>(0)
  const fallbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const insightTokensRef = useRef<string>('')
  const insightBlockIndexRef = useRef<number>(-1)

  const {
    setTitle,
    setSummary,
    addBlock,
    updateBlock,
    setError: setStoreError,
    setStatus: setStoreStatus,
    setTraceId,
    setDuration,
    clearBlocks,
    // Orchestrator-specific actions
    setThreadId,
    addNodeExecution,
    updateNodeExecution,
    setCurrentInterrupt,
    setApprovalPending,
    setEnterpriseStatus,
    setFallbackMode,
    setOrchestratorMode,
    resetOrchestratorState,
  } = useDashboardStore()

  /**
   * Handle orchestrator-specific SSE events
   */
  const handleOrchestratorEvent = useCallback(
    (eventType: string, rawData: unknown) => {
      // Clear fallback timeout on any event - orchestrator is responding
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current)
        fallbackTimeoutRef.current = null
      }

      // Server wraps all events in {event_type, data, timestamp} envelope.
      // Extract the inner `data` payload so Zod schemas match correctly.
      const isWrapped =
        typeof rawData === 'object' &&
        rawData !== null &&
        'event_type' in rawData &&
        'data' in rawData
      const data = isWrapped ? (rawData as Record<string, unknown>).data : rawData

      switch (eventType) {
        // Standard block events (same as useBlockStream)
        case 'title': {
          const parsed = parseTitleEvent(data)
          if (parsed) setTitle(parsed.title)
          break
        }
        case 'summary': {
          const parsed = parseSummaryEvent(data)
          if (parsed) setSummary(parsed.summary)
          break
        }
        case 'block': {
          const parsed = parseBlockEvent(data)
          if (parsed?.block) addBlock(parsed.block)
          break
        }
        case 'error': {
          const parsed = parseErrorEvent(data)
          if (parsed) setStoreError(`${parsed.code}: ${parsed.message}`)
          break
        }
        case 'final': {
          const parsed = parseFinalEvent(data)
          if (parsed) {
            setStoreStatus(parsed.status)
            if (parsed.trace_id) setTraceId(parsed.trace_id)
            const elapsed = Date.now() - streamStartRef.current
            if (elapsed > 0) setDuration(elapsed)
            if (parsed.error) setStoreError(parsed.error)
          }
          setIsStreaming(false)
          setIsPaused(false)
          break
        }

        // Orchestrator-specific events
        case 'node_start': {
          const parsed = parseNodeStartEvent(data)
          if (parsed) {
            const node: NodeExecution = {
              nodeId: parsed.node_id,
              nodeName: parsed.node_name,
              agentId: parsed.agent_id,
              agentName: parsed.agent_name,
              phase: parsed.phase,
              status: 'running',
              startedAt: parsed.timestamp ?? Date.now(),
            }
            addNodeExecution(node)
          }
          break
        }
        case 'node_complete': {
          const parsed = parseNodeCompleteEvent(data)
          if (parsed) {
            updateNodeExecution(parsed.node_id, {
              status: parsed.status,
              completedAt: Date.now(),
              durationMs: parsed.duration_ms,
              resultPreview: parsed.result_preview,
              error: parsed.error,
            })
          }
          break
        }
        case 'interrupt': {
          const parsed = parseInterruptEvent(data)
          if (parsed) {
            setCurrentInterrupt(parsed)
            setThreadId(parsed.thread_id)
            setIsPaused(true)
            setStoreStatus('paused')
          }
          break
        }
        case 'resumed': {
          const parsed = parseResumedEvent(data)
          if (parsed) {
            setCurrentInterrupt(null)
            setIsPaused(false)
            setStoreStatus('streaming')
          }
          break
        }
        case 'enterprise_status': {
          const parsed = parseEnterpriseStatusEvent(data)
          if (parsed) {
            setEnterpriseStatus(parsed)
          }
          break
        }
        case 'fallback': {
          const parsed = parseFallbackEvent(data)
          if (parsed) {
            setFallbackMode(true, parsed.reason)
            options.onFallback?.(parsed.reason)
          }
          break
        }
        case 'state_update': {
          const d = data as Record<string, unknown> | null
          if (d && typeof d === 'object') {
            const agentId = d.agent_id as string | undefined
            const stepId = d.step_id as string | number | undefined
            const success = d.success as boolean | undefined
            if (agentId && stepId !== undefined) {
              const nodeId = `agent-${agentId}-${stepId}`
              const existing = useDashboardStore.getState().nodeExecutions?.find(
                (n: NodeExecution) => n.nodeId === nodeId
              )
              if (!existing) {
                addNodeExecution({
                  nodeId,
                  nodeName: `Querying ${agentId}`,
                  agentId,
                  agentName: agentId.charAt(0).toUpperCase() + agentId.slice(1),
                  phase: 'execution',
                  status: success === false ? 'error' : success === true ? 'complete' : 'running',
                  startedAt: Date.now(),
                  completedAt: success !== undefined ? Date.now() : undefined,
                })
              } else if (success !== undefined) {
                updateNodeExecution(nodeId, {
                  status: success ? 'complete' : 'error',
                  completedAt: Date.now(),
                })
              }
            }
          }
          break
        }
        case 'progress': {
          // Orchestrator emits progress events: "Calling 2 agents...", "Agent X completed (1/2)"
          const d = data as Record<string, unknown> | null
          if (d && typeof d === 'object') {
            const message = d.message as string | undefined
            const agentId = d.agent_id as string | undefined
            const completed = d.completed as number | undefined
            const total = d.total as number | undefined

            if (message) {
              // Show progress as summary text
              setSummary(message)
            }

            if (agentId && completed && total) {
              // Agent completed — create/update node
              const nodeId = `progress-${agentId}`
              const existing = useDashboardStore.getState().nodeExecutions?.find(
                (n: NodeExecution) => n.nodeId === nodeId
              )
              if (!existing) {
                addNodeExecution({
                  nodeId,
                  nodeName: `${agentId} (${completed}/${total})`,
                  agentId,
                  agentName: agentId.charAt(0).toUpperCase() + agentId.slice(1),
                  phase: 'execution',
                  status: 'complete',
                  startedAt: Date.now(),
                  completedAt: Date.now(),
                })
              }
            }
          }
          break
        }
        case 'agent_called': {
          // Orchestrator emits agent_called with completion info
          const d = data as Record<string, unknown> | null
          if (d && typeof d === 'object') {
            const agentId = d.agent_id as string | undefined
            const stepId = d.step_id as string | number | undefined
            const durationMs = d.duration_ms as number | undefined
            if (agentId) {
              const nodeId = `agent-called-${agentId}-${stepId ?? 0}`
              addNodeExecution({
                nodeId,
                nodeName: `${agentId}`,
                agentId,
                agentName: agentId.charAt(0).toUpperCase() + agentId.slice(1),
                phase: 'execution',
                status: 'complete',
                startedAt: Date.now() - (durationMs ?? 0),
                completedAt: Date.now(),
                durationMs,
              })
            }
          }
          break
        }
        case 'data_ready': {
          // Data-first pattern: render tables immediately, show loader for insights
          const content = typeof data === 'object' && data !== null
            ? (data as Record<string, unknown>).content as string
            : ''
          if (content) {
            addBlock({ type: 'markdown' as const, data: { content } })
            // Add a placeholder block that signals "insights loading"
            addBlock({ type: 'markdown' as const, title: 'Key Insights', data: { content: '_Generating insights..._' } })
            insightBlockIndexRef.current = useDashboardStore.getState().blocks.length - 1
            insightTokensRef.current = ''
          }
          break
        }
        case 'text_delta': {
          // Streaming enrichment tokens — replace loader then update in-place
          const token = typeof data === 'object' && data !== null
            ? (data as Record<string, unknown>).content as string
            : ''
          if (token) {
            insightTokensRef.current += token
            const insightBlock = { type: 'markdown' as const, title: 'Key Insights', data: { content: insightTokensRef.current } }
            if (insightBlockIndexRef.current >= 0) {
              // Update the placeholder (or existing) block in-place
              updateBlock(insightBlockIndexRef.current, insightBlock)
            } else {
              // No placeholder exists — add new block
              addBlock(insightBlock)
              insightBlockIndexRef.current = useDashboardStore.getState().blocks.length - 1
            }
          }
          break
        }
        default:
          break
      }
    },
    [
      setTitle,
      setSummary,
      addBlock,
      updateBlock,
      setStoreError,
      setStoreStatus,
      setTraceId,
      setDuration,
      addNodeExecution,
      updateNodeExecution,
      setCurrentInterrupt,
      setThreadId,
      setEnterpriseStatus,
      setFallbackMode,
      options,
    ]
  )

  /**
   * Parse SSE buffer into events
   */
  const parseSSEBuffer = useCallback(
    (buffer: string): { parsed: Array<{ type: string; data: string }>; remaining: string } => {
      const events: Array<{ type: string; data: string }> = []
      const parts = buffer.split('\n\n')
      const remaining = parts.pop() || ''

      for (const part of parts) {
        if (!part.trim()) continue

        const lines = part.split('\n')
        let eventType = 'message'
        let data = ''

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7)
          } else if (line.startsWith('data: ')) {
            data += line.slice(6)
          }
        }

        if (data) {
          events.push({ type: eventType, data })
        }
      }

      return { parsed: events, remaining }
    },
    []
  )

  /**
   * Start streaming via orchestrator endpoint
   */
  const startStream = useCallback(
    async (query: string, context?: Record<string, unknown>) => {
      // Clear previous state
      clearBlocks()
      resetOrchestratorState()
      setStoreError(null)
      setStoreStatus('streaming')
      setStatus('connecting')
      setIsStreaming(true)
      setIsPaused(false)
      setError(null)
      setOrchestratorMode(true)

      // Fail fast if no token
      const token = useAuth.getState().token
      if (!token) {
        setStoreError('Authentication token missing. Please log in again.')
        setIsStreaming(false)
        setStatus('error')
        return
      }

      // Create abort controller
      abortControllerRef.current = new AbortController()
      streamStartRef.current = Date.now()

      // Set fallback timeout - if no response within FALLBACK_TIMEOUT_MS, trigger fallback
      fallbackTimeoutRef.current = setTimeout(() => {
        setFallbackMode(true, 'Orchestrator connection timeout')
        options.onFallback?.('Connection timeout')
      }, FALLBACK_TIMEOUT_MS)

      try {
        const requestBody = { query, context: context || {} }

        const response = await fetchWithAuth(`${sseBaseUrl}/api/orchestrate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
          },
          body: JSON.stringify(requestBody),
          signal: abortControllerRef.current.signal,
        })

        // Extract thread_id from headers if present
        const threadIdHeader = response.headers.get('X-Thread-ID')
        if (threadIdHeader) {
          setThreadId(threadIdHeader)
        }

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        // Clear fallback timeout - got response
        if (fallbackTimeoutRef.current) {
          clearTimeout(fallbackTimeoutRef.current)
          fallbackTimeoutRef.current = null
        }

        setStatus('connected')
        setStoreStatus('streaming')

        const reader = response.body!.getReader()
        const decoder = new TextDecoder()
        let buffer = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const { parsed, remaining } = parseSSEBuffer(buffer)
          buffer = remaining

          for (const event of parsed) {
            try {
              const data = JSON.parse(event.data)
              handleOrchestratorEvent(event.type, data)
            } catch {
              // Skip malformed SSE data
            }
          }
        }

        setStatus('disconnected')
        setIsStreaming(false)
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          setStatus('disconnected')
        } else if (err instanceof AuthExpiredError) {
          // Session expired — logout already triggered by fetchWithAuth
          setStatus('disconnected')
        } else {
          const error = err instanceof Error ? err : new Error(String(err))
          setError(error)
          setStoreError(error.message)
          setStatus('error')
        }
        setIsStreaming(false)
        setIsPaused(false)
      }
    },
    [
      sseBaseUrl,
      clearBlocks,
      resetOrchestratorState,
      setStoreError,
      setStoreStatus,
      setThreadId,
      setOrchestratorMode,
      setFallbackMode,
      handleOrchestratorEvent,
      parseSSEBuffer,
      options,
    ]
  )

  /**
   * Stop the current stream
   */
  const stopStream = useCallback(() => {
    abortControllerRef.current?.abort()
    if (fallbackTimeoutRef.current) {
      clearTimeout(fallbackTimeoutRef.current)
      fallbackTimeoutRef.current = null
    }
    setIsStreaming(false)
    setIsPaused(false)
    setStatus('disconnected')
  }, [])

  /**
   * Resume workflow with approval decision
   * POST to /api/approve — now returns SSE stream (not JSON)
   * so we can receive remaining workflow events in real-time
   */
  const resumeWorkflow = useCallback(
    async (decision: ApprovalDecision) => {
      const { threadId, currentInterrupt } = useDashboardStore.getState()

      if (!threadId || !currentInterrupt) {
        setStoreError('No active interrupt to resume')
        return
      }

      const token = useAuth.getState().token
      if (!token) {
        setStoreError('Authentication token missing. Please log in again.')
        return
      }

      setApprovalPending(true)

      try {
        const response = await fetchWithAuth(`${sseBaseUrl}/api/approve`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
          },
          body: JSON.stringify({
            thread_id: threadId,
            interrupt_id: currentInterrupt.interrupt_id,
            decision: decision.approved ? 'approve' : 'reject',
            notes: decision.notes,
            modified_params: decision.modified_params,
          }),
          signal: abortControllerRef.current?.signal,
        })

        if (!response.ok) {
          throw new Error(`Failed to resume: HTTP ${response.status}`)
        }

        // Clear approval state — the 'resumed' SSE event will also handle this
        setApprovalPending(false)
        setCurrentInterrupt(null)
        setIsPaused(false)

        if (decision.approved) {
          setStoreStatus('streaming')
          setIsStreaming(true)

          // Read SSE stream from approve response (same format as /api/orchestrate)
          const reader = response.body!.getReader()
          const decoder = new TextDecoder()
          let buffer = ''

          while (true) {
            const { done, value } = await reader.read()
            if (done) break

            buffer += decoder.decode(value, { stream: true })
            const { parsed, remaining } = parseSSEBuffer(buffer)
            buffer = remaining

            for (const event of parsed) {
              try {
                const data = JSON.parse(event.data)
                handleOrchestratorEvent(event.type, data)
              } catch {
                // Skip malformed SSE data
              }
            }
          }

          setIsStreaming(false)
          // Status is managed by the store via 'final' event handler
        } else {
          // For rejection, still read the SSE stream for 'resumed' + 'final' events
          const reader = response.body!.getReader()
          const decoder = new TextDecoder()
          let buffer = ''

          while (true) {
            const { done, value } = await reader.read()
            if (done) break

            buffer += decoder.decode(value, { stream: true })
            const { parsed, remaining } = parseSSEBuffer(buffer)
            buffer = remaining

            for (const event of parsed) {
              try {
                const data = JSON.parse(event.data)
                handleOrchestratorEvent(event.type, data)
              } catch {
                // Skip malformed SSE data
              }
            }
          }
        }
      } catch (err) {
        if ((err as Error).name === 'AbortError') return
        if (err instanceof AuthExpiredError) return // Session ended — redirect handled
        const error = err instanceof Error ? err : new Error(String(err))
        setStoreError(error.message)
        setApprovalPending(false)
        setIsStreaming(false)
      }
    },
    [
      sseBaseUrl,
      setStoreError,
      setCurrentInterrupt,
      setApprovalPending,
      setStoreStatus,
      parseSSEBuffer,
      handleOrchestratorEvent,
    ]
  )

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort()
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current)
      }
    }
  }, [])

  return {
    status,
    startStream,
    stopStream,
    resumeWorkflow,
    error,
    isStreaming,
    isPaused,
  }
}
